import { ArrowRight } from "lucide-react"
import { Link } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { MarketingLayout } from "@/components/marketing/MarketingLayout"
import { MarketingMetadata } from "@/components/marketing/MarketingMetadata"
import { ABOUT_COPY } from "@/content/marketing-about"
import { localizedHomeUrl, marketingBreadcrumbs } from "@/lib/marketing-structured-data"
import type { MarketingPageProps } from "@/types/marketing"
import founderPortrait from "../../assets/marketing/founder-portrait.webp"
import founderHiking from "../../assets/marketing/founder-hiking.webp"
import founderTravel from "../../assets/marketing/founder-travel.webp"
import founderEndurance from "../../assets/marketing/founder-endurance.webp"
import founderCycling from "../../assets/marketing/founder-cycling.webp"

const founderImages = [
  { src: founderPortrait, width: 1125, height: 1500 },
  { src: founderHiking, width: 1125, height: 1500 },
  { src: founderTravel, width: 1400, height: 933 },
  { src: founderEndurance, width: 1125, height: 1500 },
  { src: founderCycling, width: 1125, height: 1500 },
]

export default function MarketingAbout(props: MarketingPageProps) {
  const copy = ABOUT_COPY[props.locale]
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "ProfilePage",
        name: props.title,
        description: props.description,
        url: props.canonical_url,
        inLanguage: props.locale === "hr" ? "hr-HR" : "en",
        mainEntity: {
          "@type": "Person",
          name: copy.hero.name,
          jobTitle: copy.hero.role,
          description: copy.biography.paragraphs[0],
          worksFor: {
            "@type": "Organization",
            name: "Zmile d.o.o.",
            url: localizedHomeUrl(props.locale),
          },
        },
      },
      marketingBreadcrumbs({
        canonicalUrl: props.canonical_url,
        locale: props.locale,
        title: props.title,
      }),
    ],
  }

  return (
    <>
      <MarketingMetadata
        alternateUrls={props.alternate_urls}
        canonicalUrl={props.canonical_url}
        description={props.description}
        locale={props.locale}
        noindex={props.noindex}
        socialImageUrl={props.social_image_url}
        structuredData={structuredData}
        title={props.title}
      />
      <MarketingLayout
        currentPageKey={props.page_key}
        footer={props.footer}
        homeHref={props.home_href}
        languageSwitch={props.language_switch}
        locale={props.locale}
        navigation={props.navigation}
        consultationHref={props.consultation_href}
      >
        <section className="about-hero">
          <div>
            <h1 className="type-marketing-hero-display">{copy.hero.heading}</h1>
          </div>
          <div className="about-hero-intro">
            <p className="type-marketing-lead">{copy.hero.summary}</p>
            <div className="about-founder-signature">
              <span className="type-marketing-caption type-marketing-signal-display">
                {copy.hero.role}
              </span>
              <strong className="type-marketing-node-title">{copy.hero.name}</strong>
              <small className="type-marketing-microcopy type-marketing-muted">
                {copy.hero.note}
              </small>
            </div>
          </div>
        </section>

        <section className="about-collage" aria-labelledby="about-collage-label">
          <h2 className="type-marketing-caption" id="about-collage-label">
            {copy.collage.label}
          </h2>
          {copy.collage.items.map((item, index) => (
            <figure key={item.caption}>
              <img
                alt={item.alt}
                decoding="async"
                fetchPriority={index === 0 ? "high" : "auto"}
                height={founderImages[index].height}
                loading={index === 0 ? "eager" : "lazy"}
                src={founderImages[index].src}
                width={founderImages[index].width}
              />
              <figcaption className="type-marketing-caption">{item.caption}</figcaption>
            </figure>
          ))}
        </section>

        <section className="about-biography" aria-labelledby="about-biography-heading">
          <div>
            <h2 className="type-marketing-section-display" id="about-biography-heading">
              {copy.biography.heading}
            </h2>
          </div>
          <div className="about-biography-copy">
            {copy.biography.paragraphs.map((paragraph, index) => (
              <p
                className={
                  index === 0
                    ? "type-marketing-lead type-marketing-ink"
                    : "type-marketing-prose"
                }
                key={paragraph}
              >
                {paragraph}
              </p>
            ))}
          </div>
        </section>

        <section className="about-studio">
          <div className="about-studio-copy">
            <h2 className="type-marketing-section-display type-marketing-inverse">
              {copy.studio.heading}
            </h2>
            <p className="type-marketing-prose type-marketing-inverse-muted">{copy.studio.body}</p>
            <Link
              className="home-secondary-action type-marketing-action type-marketing-inverse"
              href={props.services_href}
            >
              {copy.studio.servicesLink}
              <ArrowRight className="marketing-inline-icon" aria-hidden="true" />
            </Link>
          </div>
          <ol aria-label={copy.studio.eyebrow}>
            {copy.studio.steps.map((step, index) => (
              <li key={step}>
                <span className="type-marketing-index type-marketing-signal-display">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <strong className="type-marketing-list-display type-marketing-inverse">
                  {step}
                </strong>
              </li>
            ))}
          </ol>
        </section>

        <section className="about-principles">
          <div>
            {copy.principles.items.map((principle, index) => (
              <article key={principle.title}>
                <span className="type-marketing-index type-marketing-signal-display">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <h2 className="type-marketing-list-display">{principle.title}</h2>
                <p className="type-marketing-body-small">{principle.body}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="about-final">
          <div>
            <h2 className="type-marketing-section-display">{copy.final.heading}</h2>
          </div>
          <div>
            <p className="type-marketing-prose">{copy.final.body}</p>
            <div className="about-final-actions">
              <Button asChild variant="marketing" size="marketingLg">
                <Link href={props.consultation_href} data-analytics-id="about-consultation">
                  {copy.final.review}
                  <ArrowRight className="marketing-inline-icon" aria-hidden="true" />
                </Link>
              </Button>
              <Link
                className="home-secondary-action type-marketing-action"
                href={props.work_href}
              >
                {copy.final.conversation}
              </Link>
              <p className="type-marketing-body-small">
                <Link href={props.vancleef_href}>VanCleef →</Link>{" · "}
                <Link href={props.cajanka_href}>Čajanka →</Link>
              </p>
            </div>
          </div>
        </section>
      </MarketingLayout>
    </>
  )
}
